<template>
  <div id="app">
    <header class="topbar">
      <div class="topbar-inner">
        <a class="brand" @click="goHome">
          <span class="brand-logo"><i class="el-icon-s-tools" /></span>
          <span class="brand-name">快捷工具箱</span>
        </a>
        <nav class="nav">
          <router-link to="/home" class="nav-link"><i class="el-icon-s-tools" /><span>工具箱</span></router-link>
          <router-link to="/feedback" class="nav-link"><i class="el-icon-chat-dot-round" /><span>留言</span></router-link>
          <router-link to="/games" class="nav-link"><i class="el-icon-trophy" /><span>小游戏</span></router-link>
          <router-link to="/demos" class="nav-link"><i class="el-icon-reading" /><span>教育演示</span></router-link>
        </nav>
        <div class="login-panel">
          <template v-if="authBus.isLoggedIn && authBus.user">
            <button type="button" class="user-button" @click="profileVisible = true">
              <el-avatar :size="30" :src="avatarUrl(authBus.user.avatarFilename)"><i class="el-icon-user-solid" /></el-avatar>
              <span>{{ authBus.user.nickname }}</span>
            </button>
            <el-button size="small" :loading="authLoading" @click="handleLogout">退出登录</el-button>
          </template>
          <el-button v-else type="primary" size="small" @click="loginVisible = true">登录 / 注册</el-button>
        </div>
      </div>
    </header>
    <main class="page-main">
      <div v-if="breadcrumbs.length" class="page-container">
        <div class="subpage-header">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item to="/home">工具箱</el-breadcrumb-item>
            <el-breadcrumb-item>{{ breadcrumbs[0] }}</el-breadcrumb-item>
          </el-breadcrumb>
          <el-button type="text" icon="el-icon-back" @click="goHome">返回工具列表</el-button>
        </div>
      </div>
      <router-view />
    </main>
    <SiteFooter />
    <LoginDialog :visible.sync="loginVisible" />
    <ProfileDialog :visible.sync="profileVisible" />
  </div>
</template>

<script>
import LoginDialog from './components/LoginDialog.vue'
import ProfileDialog from './components/ProfileDialog.vue'
import SiteFooter from './components/SiteFooter.vue'
import { authBus } from './store/auth'
import { logout } from './api/auth'
import { avatarUrl } from './config/avatars'

export default {
  name: 'App',
  components: { LoginDialog, ProfileDialog, SiteFooter },
  data() { return { authBus, authLoading: false, loginVisible: false, profileVisible: false } },
  computed: {
    breadcrumbs() {
      const labels = { '/calendar': '日历查询', '/file-merge': '文件合并', '/password-generator': '密码生成器', '/calendar-access': '日历接入', '/games': '小游戏', '/keyboard-clean': '键盘清洁', '/image-compare': '图片对比', '/demos': '教育演示' }
      return labels[this.$route.path] ? [labels[this.$route.path]] : []
    }
  },
  created() {
    this.authBus.$on('login-required', this.openLogin)
    this.authBus.syncLoginStatus()
    if (this.$route.query.login === 'required') this.openLogin()
  },
  beforeDestroy() { this.authBus.$off('login-required', this.openLogin) },
  watch: {
    '$route.query.login'(value) { if (value === 'required') this.openLogin() }
  },
  methods: {
    avatarUrl,
    openLogin() { this.loginVisible = true },
    goHome() { if (this.$route.path !== '/home') this.$router.push('/home') },
    async handleLogout() {
      this.authLoading = true
      try { await logout() } catch (error) { /* Always clear the local session. */ }
      authBus.logout()
      this.authLoading = false
      this.$message.info('已退出登录')
    }
  }
}
</script>

<style>
* { box-sizing: border-box; }
#app { min-height: 100vh; display: flex; flex-direction: column; }

/* 吸顶导航：白色半透明 + 毛玻璃 */
.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid var(--line);
  background: rgba(255, 255, 255, .85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
.topbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 1280px;
  height: 64px;
  margin: 0 auto;
  padding: 0 32px;
}
.brand { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.brand-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #fff;
  font-size: 17px;
  background: var(--brand);
  border-radius: var(--r-md);
}
.brand-name { color: var(--ink); font-size: 16px; font-weight: 600; }
.nav { display: flex; align-items: center; gap: 4px; }
.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  color: var(--ink-2);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  border-radius: var(--r-md);
  transition: color .14s;
}
.nav-link i { font-size: 16px; }
.nav-link:hover { color: var(--ink); }
.nav-link.router-link-active { color: var(--brand); font-weight: 600; }
.login-panel { display: flex; align-items: center; gap: 8px; }
.user-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 12px 3px 3px;
  cursor: pointer;
  color: var(--ink);
  font-size: 14px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: 999px;
}

/* 全宽通道：是否限宽由各页面自己的 page-container 决定（首页 Hero 需要通栏背景） */
.page-main { flex: 1; width: 100%; }
.subpage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--line-soft);
}
@media (max-width: 900px) {
  .topbar-inner, .page-main { padding: 0 16px; }
  .nav-link span { display: none; }
}
</style>
